/**
 * Shows files attached to a given entity. Lets users download (private)
 * or open public URL (invoice-pdfs).
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { FileText, Image as ImageIcon, Film, Download, Trash2, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';
import { formatDistanceToNow } from 'date-fns';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ICON_FOR = (type) => {
  if (!type) return FileText;
  if (type.startsWith('image/')) return ImageIcon;
  if (type.startsWith('video/')) return Film;
  return FileText;
};

const formatBytes = (b) => {
  if (!b) return '';
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  return `${(b / 1024 / 1024).toFixed(1)} MB`;
};

export const FileList = ({ attachedTo, attachedId, refreshKey = 0, canDelete = false, onDeleted }) => {
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchFiles = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/storage/files`, {
        params: { attached_to: attachedTo, attached_id: attachedId },
        withCredentials: true,
      });
      setFiles(data || []);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchFiles(); /* eslint-disable-next-line */ }, [attachedTo, attachedId, refreshKey]);

  const openFile = async (f) => {
    try {
      if (f.is_public && f.public_url) {
        window.open(f.public_url, '_blank', 'noopener');
        return;
      }
      const { data } = await axios.post(
        `${API_URL}/api/storage/signed-download-url`,
        { bucket: f.bucket, path: f.path, expires_in: 300 },
        { withCredentials: true }
      );
      window.open(data.url, '_blank', 'noopener');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to open file');
    }
  };

  const deleteFile = async (f) => {
    if (!window.confirm(`Delete ${f.filename}?`)) return;
    try {
      await axios.delete(`${API_URL}/api/storage/file`, {
        data: { bucket: f.bucket, path: f.path },
        withCredentials: true,
      });
      toast.success('Deleted');
      setFiles((prev) => prev.filter((x) => x.id !== f.id));
      onDeleted?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Delete failed');
    }
  };

  if (loading) return <div className="text-xs text-zinc-500">Loading files…</div>;
  if (files.length === 0) return <div className="text-xs text-zinc-500 italic">No files uploaded yet</div>;

  return (
    <div className="space-y-2" data-testid="file-list">
      {files.map((f) => {
        const Icon = ICON_FOR(f.content_type);
        return (
          <div key={f.id} className="flex items-center gap-3 p-2.5 bg-white/[0.03] border border-white/5 rounded-lg hover:border-white/10 transition-colors" data-testid="file-list-row">
            <Icon size={16} className="text-[#FF451A] shrink-0" />
            <div className="flex-1 min-w-0">
              <p className="text-sm text-white truncate font-medium">{f.filename}</p>
              <p className="text-[10px] text-zinc-500 font-mono uppercase tracking-wider">
                {formatBytes(f.size)} · {f.bucket} · {(() => { try { return formatDistanceToNow(new Date(f.created_at), { addSuffix: true }); } catch { return ''; } })()}
              </p>
            </div>
            <button onClick={() => openFile(f)} className="p-1.5 text-zinc-400 hover:text-[#FF451A] transition-colors" title={f.is_public ? 'Open public link' : 'Download'} data-testid="file-list-open-button">
              {f.is_public ? <ExternalLink size={14} /> : <Download size={14} />}
            </button>
            {canDelete && (
              <button onClick={() => deleteFile(f)} className="p-1.5 text-zinc-500 hover:text-[#FF3B30] transition-colors" title="Delete" data-testid="file-list-delete-button">
                <Trash2 size={14} />
              </button>
            )}
          </div>
        );
      })}
    </div>
  );
};
